import styles from './styles.module.scss';

import { useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { Router, Redirect, useLocation } from '@reach/router';

import { PageLayout } from 'components/PageLayout';
import { GIFTCARD_MENU_ITEM_ID, useMenu } from 'components/MenuContext/MenuContext';

import { useMerchant } from 'hooks/context/merchant';
import { useWindowSize } from 'hooks/breakpoints';

import { AuthenticationRoute } from 'routes/Authentication/Authentication';
import { CartRoute } from 'components/CartRoute';
import { GiftCardForm } from 'routes/GiftCard/Form';
import { BusinessInfo } from 'routes/Menu/BusinessInfo';
import { MenuCategories } from 'routes/Menu/MenuCategories';
import { MenuHeader } from 'routes/Menu/MenuHeader';
import { RunningOrderStatus } from 'components/RunningOrderStatus';
import { EditGiftCardModal, SingleGiftCardModal } from 'routes/GiftCard/Modal';
import { EditMenuItemModal } from 'routes/MenuItemModal/EditMenuItemModal';
import {
  SERVICE_HOURS_MODAL_ID,
  ServiceHoursModal,
} from 'routes/Menu/BusinessInfo/ServiceHours/Modal/ServiceHoursModal';

import { heapTrack } from 'util/Analytics';
import { AddMenuItemModal } from 'routes/MenuItemModal/AddMenuItemModal';
import { useCustomer } from 'hooks/context/customer/customer';
import { PreviousOrdersSection } from './PreviousOrdersSection/PreviousOrdersSection';
import { PreviousOrdersModal } from 'routes/PreviousOrdersModal/PreviousOrdersModal';
import { useMerchantState } from 'hooks/context/MerchantState';
import { PerksModal } from 'components/PerksModal';
import { PerksDescriptionModal } from 'components/PerksDescriptionModal';
import { MenuBanners } from 'components/MenuBanners';
import { useToggle } from '@clover-util/use-toggle';
import { OnloadPopUps } from 'components/OnloadPopUps';
import { DeliveryAddressModal } from 'routes/DeliveryAddressModal/DeliveryAddressModal';
import { DeliveryModalError } from 'routes/DeliveryAddressModal/components/DeliveryModalError';
import { OverlaySpinner } from 'components/Spinner';
import { useDeliveryValidation } from 'hooks/useDeliveryValidation';

const MenuLoadingBlocker: React.FC = () => {
  const location = useLocation();
  const deliveryValidation = useDeliveryValidation();
  const addressModalIsOpen = useMemo(() => location.pathname.split('/').includes('address'), [location.pathname]);

  if (deliveryValidation.isLoading && !addressModalIsOpen) return <OverlaySpinner />;

  return null;
};

const Menu: React.FC = () => {
  const { merchant, service, isLoading: isMerchantLoading } = useMerchant();
  const { menu, loading: isMenuLoading } = useMenu();
  const { t } = useTranslation();
  const { isDesktop } = useWindowSize();
  const { showGiftCardOnlyState, showCustomerAuthState } = useMerchantState();
  const { isLoggedIn } = useCustomer();

  // Handle onload Pop-up
  const [popUpModalVisible, togglePopUp, setIsOpen] = useToggle(false);

  useEffect(() => {
    if (!isMerchantLoading && !isMenuLoading) setIsOpen();
  }, [isMenuLoading, isMerchantLoading, setIsOpen]);

  useEffect(() => {
    if (showGiftCardOnlyState) heapTrack('gift-card-only-page-visit');
  }, [showGiftCardOnlyState]);

  return (
    <>
      <PageLayout customMainClass={styles.main} header={<MenuHeader />}>
        <Helmet>
          {merchant && (
            <title>
              {t('helmet.title', {
                merchantName: merchant.name,
                merchantCity: merchant.address.city,
                merchantState: merchant.address.state,
              })}{' '}
              | Clover
            </title>
          )}
          {merchant && <meta name="description" content={t('helmet.description', { merchantName: merchant.name })} />}
          {merchant && (
            <link
              rel="canonical"
              href={`${window.location.protocol}//${window.location.hostname}/online-ordering/${merchant?.slug}`}
            />
          )}
        </Helmet>
        <MenuBanners />

        {/* NOTE: Before you add something here, there is scss selecting the mainContainer when it is a direct sibling of Banner. */}
        <div className={styles.mainContainer}>
          <BusinessInfo />
          <OnloadPopUps visible={popUpModalVisible} onClose={togglePopUp} />
          <Router primary={false}>
            <ServiceHoursModal path={SERVICE_HOURS_MODAL_ID} service={service} timezone={merchant?.timezone} />
            <EditGiftCardModal path="gift-card/:index" />
            <EditMenuItemModal path="item/:cartId" />
            <RunningOrderStatus path="running-order" />
            {!isDesktop && <CartRoute path={'cart/*'}></CartRoute>}
            {isDesktop && <Redirect noThrow from={'cart'} to={'../'}></Redirect>}
            {!!menu?.hasGiftCard && <SingleGiftCardModal path={GIFTCARD_MENU_ITEM_ID} />}
            <PreviousOrdersModal path="previous-orders" />
            <PerksModal path="perks" />
            <PerksDescriptionModal path="perks/:uuid" />
            <DeliveryModalError path="address-error" />
            <DeliveryAddressModal path="address" />
            <AddMenuItemModal path=":itemId" />
            {showCustomerAuthState && !isLoggedIn && <AuthenticationRoute path="login/*" />}
          </Router>
          {showCustomerAuthState && isLoggedIn && <PreviousOrdersSection />}
          {showGiftCardOnlyState ? <GiftCardForm /> : <MenuCategories />}
        </div>
      </PageLayout>
      <MenuLoadingBlocker />
    </>
  );
};

export { Menu };
